"use client"

import React, { Suspense } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { LipSyncModel } from "@/app/enum"
import { Filter, X } from "lucide-react"

const FILTERS_CONFIG = [
	{ id: "person_sex", label: "Gender", options: ["Male", "Female", "Neutral"] },
	{ id: "person_age", label: "Age", options: ["Baby", "Child", "Teenage", "Young", "Middle-age", "Elder", "Old"] },
	{ id: "person_style", label: "Style", options: ["Realistic", "Cartoon", "Animal", "Film", "AI-Generated", "Non-Human"] },
	{ id: "person_purpose", label: "Purpose", options: ["Music", "Content-creator", "E-commerce", "Vlog", "Education", "Entertainment", "Podcast", "News", "Intro", "Adult"] },
]

function FilterBarContent({ hideTitle = false, className = "" }) {
	const router = useRouter()
	const searchParams = useSearchParams()

	const updateFilter = (key, value) => {
		const params = new URLSearchParams(searchParams.toString())
		if (value) {
			params.set(key, value)
		} else {
			params.delete(key)
		}
		router.push(`/showcase?${params.toString()}`, { scroll: false })
	}

	const clearAll = () => {
		router.push("/showcase", { scroll: false })
	}

	const activeFiltersCount = Array.from(searchParams.keys()).length

	return (
		<div className={`flex flex-col gap-6 ${className}`}>
			{!hideTitle && (
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2 font-bold text-lg">
						<Filter size={20} className="text-primary" />
						Filters
					</div>
					{activeFiltersCount > 0 && (
						<button onClick={clearAll} className="btn btn-ghost btn-xs gap-1 opacity-60 hover:opacity-100">
							<X size={14} /> Clear All
						</button>
					)}
				</div>
			)}

			<div className="flex flex-col gap-6">
				{/* Model Filter */}
				<div className="flex flex-col gap-2">
					<h2 className="text-xs font-black uppercase tracking-widest opacity-40 px-1">AI Model</h2>
					<div role="tablist" className="tabs tabs-box tabs-xs bg-base-100 p-2 rounded-xl w-fit flex-wrap gap-1">
						<div
							className={`tab px-2 ${!searchParams.get("fal_model") ? "tab-active" : ""}`}
							onClick={() => updateFilter("fal_model", "")}
						>
							All
						</div>
						{Object.values(LipSyncModel).map(model => (
							<h3
								key={model.id}
								name="fal_model"
								className={`tab px-2 ${searchParams.get("fal_model") === model.params.model_id ? "tab-active" : ""}`}
								aria-label={model.name}
								onClick={() => updateFilter("fal_model", model.params.model_id)}
							>
								{model.name}
							</h3>
						))}
					</div>
				</div>

				{/* Other Dynamic Filters */}
				{FILTERS_CONFIG.map(filter => (
					<div key={filter.id} className="flex flex-col gap-2">
						<h2 className="text-xs font-black uppercase tracking-widest opacity-40 px-1">{filter.label}</h2>
						<div className="tabs tabs-box tabs-xs bg-base-100 p-2 rounded-xl w-fit flex-wrap gap-1">
							<div
								className={`tab px-2 ${!searchParams.get(filter.id) ? "tab-active" : ""}`}
								onClick={() => updateFilter(filter.id, "")}
								role="tab"
								aria-label="All"
							>
								All
							</div>
							{filter.options.map(opt => (
								<h3
									key={opt}
									className={`tab px-2 ${searchParams.get(filter.id) === opt ? "tab-active" : ""}`}
									onClick={() => updateFilter(filter.id, opt)}
									role="tab"
									aria-label={opt}
								>
									{opt}
								</h3>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

export default function FilterBar({ hideTitle, className }) {
	return (
		<Suspense fallback={<div className="h-64 w-full bg-base-200 animate-pulse rounded-3xl"></div>}>
			<FilterBarContent hideTitle={hideTitle} className={className} />
		</Suspense>
	)
}
